// filepath: d:\Projet\Nouzha-Palace\frontend\src\pages\InvoiceRequest.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Mail, Phone, User, FileText, ArrowLeft, Loader } from 'lucide-react';
import invoiceRequestService from '../services/invoiceRequestService';
import reservationService from '../services/reservationService';
import { 
  ModernInput, 
  FormSection 
} from '../components/ui/FormComponents';

const InvoiceRequest = () => {
  const { id, type } = useParams();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: ''
  });
  // const [reservationDetails, setReservationDetails] = useState(null);
  const [loadingReservation, setLoadingReservation] = useState(true);
  const [reservationError, setReservationError] = useState('');

  useEffect(() => {
    const fetchReservationDetails = async () => {
      setLoadingReservation(true);
      try {
        const response = await reservationService.getReservationById(id, type);
        
        if (response.success && response.data) {
          // setReservationDetails(response.data);
          
          // Extraire l'email de la réservation selon le type de réservation
          let reservationEmail = '';
          

          
          // Recherche exhaustive de l'email dans la structure de données
          if (response.data.data && response.data.data.email) {
            // Pour les réservations qui ont un format { data: { email: '...' } }
            reservationEmail = response.data.data.email;
          } else if (response.data.data && response.data.data.emailClient) {
            // Format { data: { emailClient: '...' } }
            reservationEmail = response.data.data.emailClient;
          } else if (response.data.email) {
            // Format direct { email: '...' }
            reservationEmail = response.data.email;
          } else if (response.data.emailClient) {
            // Format direct avec emailClient
            reservationEmail = response.data.emailClient;
          } else if (response.data.utilisateur && response.data.utilisateur.email) {
            // Format avec utilisateur
            reservationEmail = response.data.utilisateur.email;
          } else if (response.data.client && response.data.client.email) {
            // Format avec client
            reservationEmail = response.data.client.email;
          } else if (response.data.compte && response.data.compte.email) {
            // Format avec compte
            reservationEmail = response.data.compte.email;
          }
          
          
          // Définir l'email du formulaire avec l'email de la réservation
          if (reservationEmail) {
            setFormData(prev => ({
              ...prev,
              email: reservationEmail
            }));
          } else {
            // Si aucun email n'est trouvé dans la réservation, utiliser celui du compte connecté
            const user = JSON.parse(localStorage.getItem('user'));
            if (user && user.email) {
              setFormData(prev => ({
                ...prev,
                email: user.email
              }));
            } else {
              console.warn('Aucun email trouvé dans la réservation ni dans le compte connecté');
            }
          }
          
          // Remplir également le nom si disponible
          const reservationName = 
            (response.data.data && response.data.data.nom) || 
            response.data.nom || 
            (response.data.utilisateur && response.data.utilisateur.nom) ||
            (response.data.client && response.data.client.nom) ||
            (response.data.compte && response.data.compte.nom) || 
            '';
            
          if (reservationName) {
            setFormData(prev => ({
              ...prev,
              name: reservationName
            }));
          } else {
            // Utiliser le nom du compte connecté si disponible
            const user = JSON.parse(localStorage.getItem('user'));
            if (user && user.username) {
              setFormData(prev => ({
                ...prev,
                name: user.username
              }));
            }
          }
          
          // Remplir le téléphone si disponible
          const reservationPhone = 
            (response.data.data && response.data.data.telephone) || 
            response.data.telephone || 
            (response.data.utilisateur && response.data.utilisateur.telephone) ||
            (response.data.client && response.data.client.telephone) ||
            (response.data.compte && response.data.compte.telephone) || 
            '';
            
          if (reservationPhone) {
            setFormData(prev => ({
              ...prev,
              phone: reservationPhone
            }));
          } else {
            // Utiliser le téléphone du compte connecté si disponible
            const user = JSON.parse(localStorage.getItem('user'));
            if (user && user.phone) {
              setFormData(prev => ({
                ...prev,
                phone: user.phone
              }));
            }
          }
        } else {
          setReservationError('Impossible de récupérer les détails de la réservation');
        }
      } catch (error) {
        console.error('Error fetching reservation details:', error);
        setReservationError('Une erreur est survenue lors de la récupération des détails de la réservation');
      } finally {
        setLoadingReservation(false);
      }
    };

    fetchReservationDetails();
  }, [id, type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Ne pas permettre la modification de l'email
    if (name !== 'email') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: '' });

    try {
      const contactInfo = {
        name: formData.name,
        email: formData.email, // L'email est celui associé à la réservation et n'est pas modifiable
        phone: formData.phone,
        message: formData.message
      };

      const result = await invoiceRequestService.createInvoiceRequest(id, type, contactInfo);
      
      if (result.success) {
        setStatus({ loading: false, success: true, error: '' });
      } else {
        setStatus({ loading: false, success: false, error: result.error || 'Une erreur est survenue' });
      }
    } catch (error) {
      setStatus({ loading: false, success: false, error: 'Une erreur est survenue lors de la demande' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 py-20 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-3xl mx-auto"
      >
        <Link to="/profile" className="inline-flex items-center text-amber-700 hover:text-amber-900 transition-colors mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour au profil
        </Link>
        
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-amber-100/50 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 px-8 py-8 relative">
            <div className="absolute inset-0 bg-black/5"></div>
            <div className="relative flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Demande de facture
              </h1>
            </div>
          </div>

          <div className="p-8">
            {status.success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Demande envoyée avec succès</h2>
                <p className="text-gray-600 mb-8">
                  Votre demande de facture a été enregistrée. Vous recevrez votre facture par email dans les prochains jours.
                </p>
                <Link
                  to="/profile"
                  className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium py-3 px-6 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-md"
                >
                  Retour à mon profil
                </Link>
              </motion.div>
            ) : loadingReservation ? (
              <div className="text-center py-12">
                <Loader className="h-10 w-10 text-amber-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Chargement des informations de réservation...</p>
              </div>
            ) : reservationError ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="h-10 w-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Erreur</h2>
                <p className="text-gray-600 mb-8">{reservationError}</p>
                <Link
                  to="/profile"
                  className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium py-3 px-6 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-md"
                >
                  Retour à mon profil
                </Link>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-8">
                  Complétez le formulaire ci-dessous pour demander une facture pour votre réservation. 
                  Notre équipe vous l'enverra par email dans les 2 à 3 jours ouvrables.
                </p>

                {status.error && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <p className="text-red-700">{status.error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  <FormSection title="Détails du demandeur" icon={User}>
                    <div className="space-y-6">
                      <ModernInput
                        label="Nom complet"
                        icon={User}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Votre nom complet"
                      />

                      <ModernInput
                        label="Email"
                        icon={Mail}
                        name="email"
                        value={formData.email}
                        readOnly
                        placeholder="Email de réservation"
                        required
                      />

                      <ModernInput
                        label="Téléphone"
                        icon={Phone}
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Votre numéro"
                      />

                      <ModernInput
                        label="Message (optionnel)"
                        icon={FileText}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        placeholder="Précisions pour votre facture..."
                      />
                    </div>
                  </FormSection>

                  <div className="bg-orange-50/50 border border-orange-100 rounded-2xl p-6">
                    <p className="text-orange-900 text-sm leading-relaxed">
                      <strong className="text-orange-600 block mb-1 uppercase tracking-widest text-[10px]">Note importante</strong>
                      La facture sera émise pour la réservation identifiée par le numéro <strong className="text-gray-900">{id}</strong>. 
                    </p>
                  </div>

                  <div className="flex justify-center pt-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={status.loading}
                      className="w-full bg-gray-900 text-white font-black uppercase tracking-widest py-5 px-8 rounded-2xl hover:bg-orange-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-2xl"
                    >
                      {status.loading ? 'Demande...' : 'Demander ma facture'}
                    </motion.button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default InvoiceRequest;